//  ----------------------------------------------------------------------------
//  Dependencies
//  ----------------------------------------------------------------------------
import { Button, Modal, Snackbar } from '@mui/material';
//  -- Components ---------------------
import { ArrowBack, Bookmark, Delete, Fullscreen, FullscreenExit, MenuOutlined } from '@mui/icons-material';
import { Alert } from '@mui/material';
//  -- Features -----------------------
import BalconCulturalTooling from 'features/BalconCulturalTooling';
import dayjs from 'utils/dayjsConfig';
import { debounce, filter, find, findIndex, get, groupBy } from 'lodash';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from "react-router-dom";
import { useReactToPrint } from 'react-to-print';
import styled from 'styled-components';
//  -- Constants ----------------------
import { LeftCircleOutlined, RightCircleOutlined } from '@ant-design/icons';
import * as Sentry from '@sentry/react';
import { Col, Dropdown, Row, Tooltip } from 'antd';
import AnswerPopup from 'components/AnswerPopup';
import PDFPopup from 'components/PDFPopup';
import ActivityStandardsPopup from 'components/ActivityStandardsPopup';
import AssignableNotificationAnswer from 'components/AssignableNotificationAnswer';
import AssignmentAiSection from 'components/AssignmentAiGenerate/AssignmentAiModal';
import Differentiation from 'components/Differentiation/Differentiation';
import DifferentiationViewModal from 'components/Differentiation/DifferentiationViewModal';
import ViewArticleLevels from 'components/ebook/levels/LevelModal';
import EbookGallleryView, { ViewGallery } from 'components/EbookGallleryView';
import { useOrientation } from 'components/hooks';
import useOrientationFullScreen from 'components/hooks/useOrientationFullScreen';
import ImageHighlighter from 'components/ImageHighlighter';
import Instructions from 'modules/instructions/teacher/Instructions';
import Loading from 'components/Loading';
import MainbookPopup from 'components/MainbookPopup';
import ClickOutside from 'components/OnClickOutside';
import StoryAIPopup from 'components/StoryAIPopup';
import CreateStoryModal from 'components/StoryWithAi/CreateStoryModal';
import StoryViewModal from 'components/StoryWithAi/StoryViewModal';
import LessonViewModal from 'components/SuperLesson/LessonViewModal';
import SuperLessons from 'components/SuperLesson/SuperLesson';
import TwoSidePages from 'components/TwoSideView/TwoSidePages';
import { useAlert } from 'components/UI/alertSakeBar/alertProvider';
import Vocabilary from "modules/word_bank/teacher/vocab/Vocabilary";
import Phrases from "modules/word_bank/teacher/Phrases/Phrases";
import { useFullScreenTabDetect, useMobileDetect, useTabletDetection } from 'containers/chat/hooks';
import ShowBookPreviewExtensionActivity from 'modules/extraResource/teacher/showBookPreviewExtensionActivity';
import SentenceStarterPopUp from 'modules/sentenceStarter/teacher';
import SentenceStarterBookPopUp from 'modules/sentenceStarter/teacher/sentenceStarterBookPopUp';
import SlideWidget from 'modules/slideWidgetModule/slideWidget';
import { useDispatch } from 'react-redux';
import { useBookmarks } from 'store/bookmark/selectors';
import { handleCreateBookmarks, handleDeleteBookmarks, handleUpdateBookmarks, handleListClassBookmarks } from 'store/bookmark/thunks';
import { useAllBook } from 'store/books/selectors';
import { useBookUnits } from 'store/booksUnits/selectors';
import { useCurrentClass } from 'store/classes/selectors';
import { useAiAnswerView, useAnswerView, useMainbookView, usePDFView, useActivityStandardsView } from 'store/main_book_popup/selectors';
import { useSchoologyGradeCategory } from 'store/schoologyauth/selectors';
import { openSlideWidgetAction } from 'store/slides/actions';
import { useGetAllSlidesData } from 'store/slides/selectors';
import { useChapterArticleLevel, useOneStoryWithAi } from 'store/StoryAiCofig/selectors';
import { useTimmerRunning } from 'store/timmer/selectors';
import { updateUI } from 'store/UI/actions';
import { useUI } from 'store/UI/selectors';
import COLOR from 'utils/constants/color';
import { useFullScreen } from 'utils/context/FullScreenProvider';
import { useSideDrawer } from 'utils/context/SideDrawerProvider';
import { generateUrlForNonEbook, getBackToUrl } from "utils/lib/ebookHelper";
import { addPrintableClass, removePrintableClass } from 'utils/methods/textMethod';
import CognatesHandler from 'modules/word_bank/teacher/cognates';
import { debounceMath } from 'utils/methods/math';
import usePopUpAtOnce from 'components/hooks/usePopUpAtOnce';
import useSoyHistoriasNotification from 'components/hooks/useSoyHistoriasNotification';
import SuperLessonDiffList from 'modules/superLessonDiffList';
import { handleGetDistrictTeacher } from 'store/users/thunks';
import { useAllDistrictTeacher } from 'store/users/selectors';
import { useMatchMakingView } from 'store/main_book_popup/selectors';
import { handleLearningModuleCount } from 'store/bingo/thunks';
import { isErrorContent } from "utils/methods/storyContentValidation";
import SentryWrap from 'components/Sentry';
import useStoryViewOpen from 'components/hooks/useStoryViewOpen';
const cache_random =Math.floor(Math.random()*1000000);
//  ----------------------------------------------------------------------------
//  Styles
//  ----------------------------------------------------------------------------
const ButtonContainer = styled.button`
  background-color: transparent;
  border: none;
  color: ${COLOR.black600};
  cursor: pointer;
  outline: none;
  position: fixed;
  top: 50%;
  transition: all 300ms ease;
  z-index: 100;

  ${({ left }) => left !== undefined && `left: ${left}px;`}
  ${({ right }) => right !== undefined && `right: ${right}px;`}

  @media only screen and (max-width: 768px) {  // Mobile
    ${({ mobileLeft }) => mobileLeft !== undefined && `left: ${mobileLeft}px;`}
    ${({ mobileRight }) => mobileRight !== undefined && `right: ${mobileRight}px;`}
  }

  @media only screen and (min-width: 769px) and (max-width: 1024px) { // Tablet
    ${({ tabletLeft }) => tabletLeft !== undefined && `left: ${tabletLeft}px;`}
    ${({ tabletRight }) => tabletRight !== undefined && `right: ${tabletRight}px;`}
  }

  @media only screen and (min-width: 1025px) { // Desktop
    ${({ left }) => left !== undefined && `left: ${left}px;`}
    ${({ right }) => right !== undefined && `right: ${right}px;`}
  }
`;

const Container = styled.div`
    align-items: stretch;
    display: flex;
    flex-direction: column;
    margin: 0px auto;
    max-width: 1320px;
    padding: 50px 0px 0px;
    position: relative;
    width: 100%;

    // @media (min-width: 1100px) {
    //     flex-direction: row;
    //     padding: 0px 0px;
    // }
      @media (min-width: 809px) {
        flex-direction: row;
        // padding: 0px 30px;
        gap:20px;
    }
     @media (max-width:1200px) {
        // flex-direction:column;
     }
`;

const Images = styled.img`
    background-color: ${COLOR.white};
    box-shadow: 0 10px 20px rgba(0,0,0,0.19), 0 6px 6px rgba(0,0,0,0.23);
    height: auto;
    width: 100%;
`;

const ImageContainer = styled.div`
    height: auto;
    // flex: 1;
    display: flex;
    // flex-direction: column;
    position:relative;
    transition: all 0.3s ease-in-out;
    width: ${props => props.isDrawerOpen ? '0%' : '100%'};
    opacity: ${props => props.isDrawerOpen ? '0' : '1'};
    overflow: visible;
    visibility: ${props => props.isDrawerOpen ? 'hidden' : 'visible'};
    display: ${props => props.isDrawerOpen ? 'none' : 'block'};
`;

const NavContainer = styled.div`
    display: none;

    @media (min-width: 997px) {
        align-items: flex-end;
        display: flex;
        flex-direction: column;
        margin-right: 16px;
        width: 24px;
    }
`;

const NavItemOverflow = styled.div`
    display: inline-table;
    font-size: 0.95em;
    margin-right: 1px;
    margin-top: 1px;
    overflow: hidden;
    transition: width 300ms ease 500ms;
    width: 60px;
`;

const NavItem = styled.button`
    background-color: ${p => p.active ? COLOR.indigo100 : COLOR.green900};
    border: none;
    border-radius: 2px;
    box-shadow: ${p => p.active && '0 3px 6px rgba(0,0,0,0.16), 0 3px 6px rgba(0,0,0,0.23)'};
    color: ${p => p.active ? COLOR.white : COLOR.white};
    cursor: pointer;
    /* display: initial; */
    flex-direction: row;
    font-size: 0.85em;
    font-weight: 600;
    margin-bottom: 4px;
    outline: none;
    padding: 8px;
    transition: all 300ms ease;

    &:hover {
        box-shadow: ${p => p.active && '0 3px 6px rgba(0,0,0,0.16), 0 3px 6px rgba(0,0,0,0.23)'};
    }

    &:hover ${NavItemOverflow} {
      /*   width: ${p => p.active ? '8px' : '49px'}; */
        width: '49px'};
    }
`;

const ToolsContainer = styled.div`
    height: 100%;
    margin-top: 2px;
    width: 100%;
    flex: 1;
    // max-width: 469px;
    max-width: 485px;

    @media (max-width: 1100px){
        max-width: unset;
    }
    @media (min-width: 1100px) {
        // margin-top: ${p => p.isTeacher ? "-28px" : "-3px"}
        // margin-top: ${p => p.isTeacher ? "-28px" : "-28px"}
    }
    @media (min-width: 992px) and (max-width: 1280px){
        width: 377px;
        flex: unset;
        margin-top: 0px;
    }
    @media (min-width: 809px) and (max-width: 991px){
        width:355px;
        flex: unset;
        margin-top: 0px;
    }
    @media (max-width: 808px){
        margin-top:60px;
    }
    @media (max-width: 590px){
      margin-top: 65px;
    }
`;

const Wrapper = styled.div`
    display: flex;
    // padding: 100px 0px;
    position: relative;
    width: 100%;

    @media (max-width: 768px) {
        padding: 0px 0px !important;
    }
`;
const ItemWrapper = styled.div`
    display: flex;
   flex-direction:row;
  
`;
const ListItem = styled.li`
    list-style-type: none;
    display: block;
    background-color: ${p => p.active ? COLOR.indigo100 : "#006b6e"};
    border: none;
    border-radius: 2px;
    box-shadow: ${p => p.active && '0 3px 6px rgba(0,0,0,0.16), 0 3px 6px rgba(0,0,0,0.23)'};
    color: ${p => p.active ? COLOR.white : COLOR.white};
    cursor: pointer;
    /* display: initial; */
    flex-direction: row;
    font-size: 0.75em;
    font-weight: 600;
    margin-bottom: 4px;
    outline: none;
    padding: 8px;
    transition: all 300ms ease;

    &:hover {
        box-shadow: ${p => p.active && '0 3px 6px rgba(0,0,0,0.16), 0 3px 6px rgba(0,0,0,0.23)'};
    }

    &:hover ${NavItemOverflow} {
    /*   width: ${p => p.active ? '8px' : '49px'}; */
        width: '49px'};
    }
`
const List = styled.ul`
    padding: 0px;
    margin-bottom:-10px;
    display: block;
    width: 100%;
`
const BackButton = styled.button`
    background-color: ${COLOR.indigo200};
    width: max-content;
    color: ${COLOR.white};
    border: none;
    outline: none;
    padding: 5px 0px;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    padding-left: 8px;
    padding-right: 8px;
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
    @media (max-width: 450px) {
        padding-left: 0px;
    padding-right: 3px;
    }
   `
const BookmarkButton = styled(Button)`
    margin-right: 4px;
    font-size: 10px !important;
    padding-top: 2px !important;
    padding-bottom: 2px !important;
   `;
const BookmarkLinkWrapper = styled.div`
    color: rgba(0, 0, 0, 0.87);
    font-size: 0.875rem;
    box-sizing: border-box;
    transition: background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,border 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
    font-family: "Roboto", "Helvetica", "Arial", sans-serif;
    font-weight: 500;
    line-height: 1.75;
    border-radius: 0px;
    letter-spacing: 0.02857em;
    text-transform: uppercase;
    display:flex;
    flex-direction:row;
`;
const TrashButton = styled.button`
    padding: 2px;
    border: none;
    justify-content: center;
    align-items: center;
    display: flex;
    cursor: pointer;
`;
const MarkButton = styled.button`
    padding: 2px;
    border: none;
    justify-content: center;
    align-items: center;
    display: flex;
    cursor: pointer;
`;
const AddBookmarkButton = styled.button`
    padding: 2px;
    border: none;
    justify-content: center;
    align-items: center;
    display: flex;
    cursor: pointer;
    background-color:${COLOR.green};
    padding-right: 10px;
    padding-left: 5px;
    color: #fff;
    border-radius: 0px;
    font-size: 12px;
    width: 40px;
    height: 36px;
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
`;
const ModalContent = styled.div`
    align-items: center;
    background-color: ${COLOR.white};
    border-radius: 3px;
    display: inline-flex;
    flex-direction: column;
    left: 50%;
    max-width: 320px;
    padding: 16px;
    position: absolute;
    text-align: center;
    top: 50%;
    transform: translate(-50%, -50%);
`;

const ModalRow = styled.div`
    align-items: center;
    display: flex;
    flex-direction: row;
    justify-content: center;
    margin-top: 16px;
    width: 100%;
`;
const IdeaRow = styled.div`
   display: flex !important;
   flex-direction: column !important;
   justify-content: flex-start !important;
`;
const BookMarkWrapper = styled.div`
  display: flex !important;
`;
const ActivityBtn = styled.button`
    border: none;
    justify-content: center;
    align-items: center;
    display: flex;
    cursor: pointer;
    background-color: #012B70;
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
    color: #fff;
    // border-radius: 0px;
    font-size: 12px;
    height: 36px;
    width: 55.8px;
    padding-inline: 11px;
    padding-top: 10px;
    padding-bottom: 9px;
    margin-left:2px;
`;
const FullScreenBtn = styled.button`
    border: none;
    justify-content: center;
    align-items: center;
    display: flex;
    cursor: pointer;
    background-color: #012B70;
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
    color: #fff;
    // border-radius: 0px;
    font-size: 12px;
    height: 36px;
    width: 40.8px;
    padding-inline: 11px;
    padding-top: 10px;
    padding-bottom: 9px;
    margin-left:2px;
`;
const DropdownWrapper = styled.div`
  position: absolute;
  top: 38px;
  z-index: 990;
  background: #ffffff;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.12);
  border-radius: 6px;
  padding: 8px 0;
  min-width: 260px;
`;
const DropdownItem = styled.div`
  padding: 8px 12px;
  font-size: 14px;
  font-weight: 500;
  color: #1e1e1e;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #f4f4f4;
  }

  &:not(:last-child) {
    border-bottom: 1px solid #f0f0f0;
  }
`;


//  ----------------------------------------------------------------------------
//  Feature
//  ----------------------------------------------------------------------------
//
//  Ebook
//
//  -- Description
//  Ebook display component
//
//  -- Props
//  ...
//
//  -- State
//  ...
//
//  -- Methods
//  ...
//
//  -- Usage
//  ...
//
const BalconLayout = ({
    classUsers,
    activity,
    data,
    page,
    quiz,
    ebookFlag,
    setEbookFlag,
    ebookFlagTeacherEdition,
    setEbookFlagTeacherEdition,
    tips,
    existTeacherEdition,
    existShowAnswer,
    submissions,
    currentUser,
    classActivities,
    classQuizzes,
    setPage,
    id,
    bookData,
    backToLabel,
    CHAPTER_ID,
    lessonPlan,
    rubrics,
    assignmentPlan,
    workbook_plan,
    main_book,
    unitExam,
    level,
    SuperLesson = [],
    DifferentiationLesson = [],
    chapter = "",
    ai_content,
    book
}) => {
    const {checkPopUpAtOnce,updatePopUpAtOnce} = usePopUpAtOnce();
    const isStoryViewOpen = useStoryViewOpen();
    const isTablet = useFullScreenTabDetect();
    const showAlert = useAlert();
    const landscapeOrientation = useOrientationFullScreen();
    const { isDrawerOpen, openDrawer, closeDrawer, toggleDrawer } = useSideDrawer();
    const { currentFullScreen, FullScreenMode, ExitFullScreenMode } = useFullScreen();
    const [checkInstructionDataForStudent, setCheckInstructionDataForStudent] = useState(false);
    const [instModal, setInstModal] = useState(false);
    const [imageConfiguration, setImageConfiguration] = useState({});
    const [showSpecialSupport, setShowSpecialSupport] = React.useState(false);
    const [studentViewFlag, setStudentViewFlag] = React.useState(false);
    const [printLoader, setPrintloader] = useState(false)
    const [removeBookMark, setRemoveBookMark] = useState(false);
    const [deleteBookmarkloader, setDeleteBookmarloader] = useState(false)
    const [superLessonOpen, setSuperLessonOpen] = useState(false);
    const [differentiationOpen, setDifferentiationOpen] = useState(false);
    const [storyCreateOpen, setStoryCreateOpen] = useState(false);
    
    const [openSuperLessonToolModal, setOpenSuperLessonToolModal] = useState(false);
    const [openDiffernationToolModal, setOpenDiffernationToolModal] = useState(false);
    const [assignmentAiModal, setassignmentAiModal] = useState(false);

    const [hasStoryWithAi, setHasStoryWithAi] = useState({});
    const [openStoryWithAi, setOpenStoryWithAi] = useState(false);
    const [openStoryWithAiView, setOpenStoryWithAiView] = useState(false);

    const [handleStoryId, setHandleStoryId] = useState('');
    const [handleStoryStream, setHandleStoryStream] = useState('');
    const [handleStoryStreamTitle, setHandleStoryStreamTitle] = useState('');

    const [showStoryModal, setShowStoryModal] = useState(false);

    const [isStream, setIsStream] = useState(false);
    const { matchMakingView , matchMakingFullScreenOpen, revokeMatchMakingFullScreenClose} = useMatchMakingView();
    const [isMatchMakingAssignment, setIsMatchMakingAssignment] = useState(matchMakingView);

    const orientation = useOrientation();
    const booksAllList = useAllBook()
    const getStoryWithAi = useOneStoryWithAi();
    const allDistrictTeacher = useAllDistrictTeacher();
    const dataBook = find(booksAllList, ['id', CHAPTER_ID]);
    const bookHyperlinks = get(dataBook, "hyperlinks")
    const pageHyperlink = get(find(bookHyperlinks, p => p.page == page), "links")
    const backBtnRef = useRef(null)
    const prevPageRef = useRef(page);
    const prevChapterRef = useRef(chapter);
    const forwardBtnRef = useRef(null)
    const location = useLocation();
  							  const navigate = useNavigate();
    const timmerRef = useRef(null)
    const timeIntRef = useRef(0)
    const timeStoreRef = useRef('time_to_calc')
    const clearPreviewRef = useRef(null)
    const [timeInt, setTimeInt] = useState(0)
    const submitRef = useRef(null)
    const chapterActivities = groupBy(classActivities, 'data.chapter');
    const chapterQuizzes = groupBy(classQuizzes, 'data.chapter');
    const { revokeAnswerView } = useAnswerView();
    const { revokePDFView } = usePDFView();
    const { revokeMainbookView } = useMainbookView();
    const { revokeActivityStandardsView } = useActivityStandardsView();
    const classBookmark = useBookmarks()
    const [, setTimmerRunning, clearTimmerRunning] = useTimmerRunning()
    const content  = useMemo(()=>data?.content||[],[data]);
    const dataContent =  useMemo(()=>content.slice(content.findIndex(k=>k.pageId == parseInt(bookData.start, 10)),content.findIndex(k=>k.pageId ==parseInt(bookData.finish, 10))+1 ),[content])
    const page_1game_ai_content = useMemo(() => (find(ai_content, k => parseInt(k.start_page) <= parseInt(page) && parseInt(k.end_page) >= parseInt(page))), [ai_content, page])
   const [isNavigating, setIsNavigating] = useState(false);
    const [,,,listAllclassSchoologyGradeCategory] = useSchoologyGradeCategory()
    const bookBookmarks = find(classBookmark, b => {
        const start = parseInt(bookData.start, 10);
        const end = parseInt(bookData.finish, 10)
        const markPage = parseInt(b.page, 10);
        if (b.bookId != CHAPTER_ID) {
            return false;
        } else if (markPage > start && markPage < end) {
            return true;
        } else if (markPage == start || markPage == end) {
            return true;
        } else {
            return false;
        }
    })
    const isCanvasUser = !!get(currentUser, "canvas_id")
    const [bookmarkloader, setbookmarloader] = useState(false)
    const bookUnitList = useBookUnits()
    const BALCON_PAGES = useMemo(() => get(find(bookUnitList, ["bookId", CHAPTER_ID]), "pages", []), [bookUnitList,CHAPTER_ID])
    const getNumberMask = useCallback((_page) => {
        const balconPage = BALCON_PAGES.find(p => {
            const startNum = parseInt(p.start, 10);
            const finishNum = parseInt(p.finish, 10);
            const pageNum = parseInt(_page, 10);
            return startNum <= pageNum && finishNum >= pageNum;
        });
        if (balconPage) {
            const pageNum = parseInt(_page, 10);
            const startNum = parseInt(balconPage.start, 10);
            return pageNum - startNum + 1;
        }
        
        const mask = _page - parseInt(bookData.start, 10) + 1;
        return mask;
    }, [CHAPTER_ID, BALCON_PAGES, bookData, dataBook, backToLabel])
    const currentClass = useCurrentClass()
    const dispatch = useDispatch()
    const book_tips = useMemo(() => filter(tips, x => x.bookId == CHAPTER_ID), [CHAPTER_ID]);
    const page_tips = useMemo(() => filter(book_tips, x => x.page == page && x.bookId == CHAPTER_ID), [book_tips, page]);

    const generateUrl = (page) => {
        return generateUrlForNonEbook(id, page, activity, quiz, backToLabel, level, CHAPTER_ID)
    }
    const getAllSlideData = useGetAllSlidesData();
    const page_slide = useMemo(() =>getAllSlideData?.find((x) => x.book_id == chapter && x.page_id == page) || {}, [chapter, page, getAllSlideData])
    const { revokeAiAnswerView } = useAiAnswerView();
            
    //    Article state start
    const article_levels = useChapterArticleLevel();
    const book_articles = useMemo(() => Array.isArray(article_levels) ? article_levels.filter((item) => item.chapter == chapter) : [], [chapter, article_levels]);
    const page_articles = useMemo(() => Array.isArray(book_articles) ? book_articles.filter((item) => item.page == page) : [], [book_articles, page]);
    const [openArticleLevel, setOpenArticleLevel] = useState(false);
    const UI = useUI();
    // artilce_state end

    const fetchDistrictData = async () => {
      try {
        if (currentUser?.orgId && currentUser?.id && currentUser?.role === 'teacher' && allDistrictTeacher.length === 0) {
          dispatch(handleGetDistrictTeacher(currentUser?.orgId, currentUser?.id));
        }
      } catch (error) {
        console.log(error, 123);
      }
    };

    useEffect(() => {
      fetchDistrictData();
    }, [currentUser]);

    useEffect(() => {
        if (UI?.soyHistoriasView?.isNotification) {
            setOpenStoryWithAi(true);
            setOpenStoryWithAiView(true);
        }
    }, [UI?.soyHistoriasView]);

    useEffect(() => {
        if (openArticleLevel) {
            setOpenArticleLevel(false);
        }
    }, [page])
    const handlerOpen = async (value) => {
        const widgetData = {
            open: true,
            pageId: page,
            bookId: chapter,
            data: page_slide
        };
        await dispatch(openSlideWidgetAction(widgetData));
    };

    const slideWidget = useMemo(() => {
        const checkSlides = page_slide?.pages?.every((item) => item.src === "");
  
        if (checkSlides || checkSlides === undefined || !page_slide) {
          return null;
        } else {
          return (
            <>
              {currentUser?.role !== "student" ? (
                <SlideWidget bookId={chapter} pageId={page} />
              ) : null}
            </>
          );
        }
      }, [chapter, page, page_slide]); 
  
        
    // const generateUrl = (page) => {
    //     if(backToLabel == 'Balcón cultural')
    //         return `/balconCulturalBook?id=${id}&page=${page}&activity=${activity ?? null}&quiz=${quiz ?? null}`
    //     if(backToLabel == '1nformate lessons')   
    //         return `/informateBook?id=${id}&page=${page}&activity=${activity ?? null}&quiz=${quiz ?? null}`;
    //     if(backToLabel == 'Comunidad')   
    //         return `/ComunidadBook?id=${id}&page=${page}&activity=${activity ?? null}&quiz=${quiz ?? null}`;
    //     if(backToLabel == 'Conectándome')   
    //         return `/ConectandomeBook?id=${id}&page=${page}&activity=${activity ?? null}&quiz=${quiz ?? null}`;
    //     if(backToLabel == 'AP')   
    //         return `/apBook?chapter=${CHAPTER_ID}&id=${id}&page=${page}&activity=${activity ?? null}&quiz=${quiz ?? null}`;
    //     if(backToLabel == 'Stories')  {
    //         return  `/stories?id=${id}&level=${level}&page=${page}&activity=${activity ?? null}&quiz=${quiz ?? null}`;
    //     } 

    // }


    // let tipsFiltered = [];
    // if(tips.length != undefined){
    //     tipsFiltered =filter(tips,x => x.page === page);
    // }
    const [open, setOpen] = React.useState(false);
    const [popupMessage, setPopupMessage] = React.useState('');
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const isTablate = useTabletDetection();
    const isMobile = useMobileDetect();
    const isTabletFull = useFullScreenTabDetect();
    useLayoutEffect(() => {
        closeDrawer();
    }, [orientation]);

    const showCopyRight = () => {
        setPopupMessage("Content is protected!")
        setOpen(true);

    }

    useEffect(() => {
      const filterByPage = filter(
        getStoryWithAi,
        (item) => {
          const pageMatch = parseInt(item.page) === parseInt(page);
          const isNotError = !isErrorContent(item?.story_content || '');
          return pageMatch && isNotError;
        }
      ) || {};
      setHasStoryWithAi(filterByPage);
    }, [page, getStoryWithAi]);

    useEffect(() => {
        const fetchBookmarks = async () => {
            if (currentClass?.id) {
                await dispatch(handleListClassBookmarks(currentClass.id));
            }
        };
        fetchBookmarks();
    }, [currentClass?.id, CHAPTER_ID, dispatch]);
    
    const handleOpenStoryWithAi = (type) => {
        if(!type && checkPopUpAtOnce()) return;
        updatePopUpAtOnce(true);
        if (window.innerWidth >= 744) {
            if (superLessonOpen) {
                setPopupMessage("Please close the SuperLesson, to open the Story AI");
                setOpen(true);
                return;
            } else if (differentiationOpen) {
                setPopupMessage("Please close the Differentiation, to open the Story AI");
                setOpen(true);
                return; 
            }
        }
        if (hasStoryWithAi?.length > 0 && type !== "more_story") {
            setOpenStoryWithAi(true);
            setOpenStoryWithAiView(true);
        } else {
            setShowStoryModal(true);
        }
        setToolsDropdown(false);
        setStoryCreateOpen(!storyCreateOpen);
    };

    const handleClose = () => {
        setOpen(false);
    };
   
       const getSrcPage = useCallback((page) => {
           let dataPage = find(data?.content, x => x.pageId == Number(page));
           let url = "";
           if (dataPage) {
            url = dataPage.src;
            if(url.includes("imagedelivery.net")){
                if(url.includes("?")){
                    url = url + "&quality=100&format=jpeg&cache=" + cache_random;
                }else{
                    url = url + "?quality=100&format=jpeg&cache=" + cache_random;
                }
            }
           }
           return url;
       }, [data, page, ebookFlagTeacherEdition, ebookFlag])
    const getImageRef = () => {
        return imageRef.current;
    }
    const setFlagsFalse = () => {
        setStudentViewFlag(false);
        setShowSpecialSupport(false);
        setEbookFlag(false);
        setEbookFlagTeacherEdition(false);
    }

    const imageRef = useRef();
    const handleImagePrint = () => {
        addPrintableClass()
        printCall()
    }
    const printCall = useReactToPrint({
        contentRef: imageRef,
        onBeforeGetContent: async () => { setPrintloader(true) },
        onBeforePrint: async () => {
            setPrintloader(false)
        },
        onAfterPrint:async ()=>{
            removePrintableClass()
        }
    });
    useEffect(()=>{
        listAllclassSchoologyGradeCategory()
    },[])
   useSoyHistoriasNotification({
     page,
     chapter,
     hasStoryWithAi,
     setHasStoryWithAi,
     openStoryWithAi,
     setOpenStoryWithAi,
     setOpenStoryWithAiView,
   });

    const checkKey = useCallback(debounceMath((e) => {
        try {
            if (isNavigating) return;
            let activeElement = document.activeElement;
            e = e || window.event;
            let isLessonPlan = document.getElementsByClassName('lessonPlanPopUp').length > 0;
            let isDrawerOpen = document.getElementsByClassName('ant-drawer-open').length > 0;
            let isModalOpen = document.getElementsByClassName('ant-modal-mask').length > 0;
            let isRubrics = document.getElementsByClassName('rubricsPopUp').length > 0;
            let isMainBook =
                document.getElementsByClassName('main-book-popup printScreenPage').length > 0;
            let bubbleNotes = document.getElementsByClassName('disable-key-navigation').length > 0;
            let worksheetView = document.getElementsByClassName('worksheet-play-modal').length > 0;
            let disableArrow = false;
            const inputType = ['text', 'search', 'email', 'number', 'password'];
            if (
                (activeElement.tagName === 'INPUT' && inputType.includes(activeElement.type)) ||
                activeElement.tagName === 'TEXTAREA' ||
                activeElement.classList?.contains('sun-editor-editable')
            ) {
                disableArrow = true;
            }
            if (
                isDrawerOpen ||
                isLessonPlan ||
                isRubrics ||
                isModalOpen ||
                isMainBook ||
                bubbleNotes ||
                disableArrow ||
                worksheetView
            )
                return;
            if (isDrawerOpen) return;
            if (e.keyCode == '37') {
                if (backBtnRef.current && !backBtnRef.current.disabled) {
                    setIsNavigating(true);
                    backBtnRef.current.click();
                    setTimeout(() => setIsNavigating(false), 300);
                }
            } else if (e.keyCode == '39') {
                if (forwardBtnRef.current && !forwardBtnRef.current.disabled) {
                    setIsNavigating(true);
                    forwardBtnRef.current.click();
                    setTimeout(() => setIsNavigating(false), 300);
                }
            }
        } catch (error) {
            console.log(error);
            setIsNavigating(false);
        }
    }, 150), [isNavigating]);

    useEffect(() => {
        document.addEventListener('keydown', checkKey);
        return () => {
            document.removeEventListener('keydown', checkKey);
        }
    }, [checkKey])
    useEffect(() => {
        preloadImage()
    }, [data])
    useEffect(() => {
        if (currentUser?.role != 'student') return;
        try {
            let actAct = find(chapterActivities[CHAPTER_ID], x => (get(x, "data.id") === activity))
            let actqz = find(chapterQuizzes[CHAPTER_ID], x => (get(x, "data.id") === quiz))
            if(!actqz){
                actqz = find(chapterActivities[CHAPTER_ID], x => (get(x, "data.id") === quiz));
            }
            let Actdata = find(submissions, ['assignmentId', get(actAct, "activityId")])
            let qzdata = find(submissions, ['assignmentId', get(actqz, "quizId")])
            let _timmer = 0
            let _durration = 0;
            if (activity && typeof activity === "string" && activity != "null" && activity != "undefined" && actAct) {
                _durration = actAct.timeDuration
                timeStoreRef.current = activity
                let past_time = sessionStorage.getItem(timeStoreRef.current)
                if (Actdata) {
                    if (!Actdata.complete) {
                        _timmer = Actdata.timmer
                    }
                    if (past_time > 0) {
                        _timmer = past_time
                    }
                    handleTimmer(_durration, _timmer)
                } else {
                    if (past_time > 0) {
                        _timmer = past_time
                    }
                    handleTimmer(_durration, _timmer)
                }

            } else if (quiz && typeof quiz === "string" && quiz != "null" && quiz != "undefined" && actqz) {
                _durration = actqz.timeDuration
                timeStoreRef.current = quiz
                let past_time = sessionStorage.getItem(timeStoreRef.current)
                if (qzdata) {
                    if (!qzdata.complete) {
                        _timmer = qzdata.timmer

                    }
                    if (past_time > 0) {
                        _timmer = past_time
                    }
                    handleTimmer(_durration, _timmer)
                } else {
                    if (past_time > 0) {
                        _timmer = past_time
                    }
                    handleTimmer(_durration, _timmer)
                }
            }

        } catch (error) {
            Sentry.captureException(`514 Error in useEffect: ${error}`);
            console.log(error, "errr")
        }

    }, [activity, quiz, classActivities, classQuizzes])
    const handleTimmer = (timeDuration, timer) => {
        try {
            if (timeDuration && !isNaN(parseInt(timeDuration)) && parseInt(timeDuration) > 0) {
                let _timer = (typeof timer === "number" && timer > 0) ? timer 
                : (parseInt(timer) > 0 ? parseInt(timer) 
                : parseInt(timeDuration) * 60);
                if (timmerRef.current) { onFinish(true) };
                timeIntRef.current = _timer;
                setTimmerRunning()
                timmerRef.current = setInterval(() => {
                    if (timeIntRef.current > 0) {
                        timeIntRef.current = timeIntRef.current - 1
                        let timePrint = dayjs.utc(timeIntRef.current * 1000).format('HH:mm:ss');
                        if(timeStoreRef.current){
                            sessionStorage.setItem(timeStoreRef.current, timeIntRef.current)
                        }
                        setTimeInt(timePrint)
                    } else {
                        if (typeof submitRef.current == "function") {
                            submitRef.current(true, null, false, null, true)
                            submitRef.current = null
                            onFinish()
                        }
                    }
                }, 1000)
            }
        } catch (error) {
            Sentry.captureException(`515 Error in handleTimmer: ${error}`);
            console.log(error, "error in effect");
        }
        return () => {
            onFinish(true)
        }
    }
    const onFinish = (restrict_clear_time) => {
        clearInterval(timmerRef.current)
        setTimeInt(0)
        timeIntRef.current = 0
        if(!restrict_clear_time){
            sessionStorage.removeItem(timeStoreRef.current)
            timeStoreRef.current = null
        }
        timmerRef.current = null
        clearTimmerRunning()
    }
    const setSubmitRef = (ref) => {
        submitRef.current = ref
    }
    const preloadImage = () => {
        let cPage = parseInt(page, 10) + 1
        if (cPage + 3 > data?.content?.length) return;
        for (let i = cPage; i < cPage + 5; i++) {
            let url = getSrcPage(i)
            if (url) {
                let randoming = new Image();
                randoming.src = url
                randoming.loading ="eager"
                randoming.decode = "sync"
                randoming.fetchPriority = "high"
            }
        }
    }
    const handleForDistrictAdmin = () =>{
        if (currentUser.role === 'admin') {
            setPopupMessage('Please login as teacher!');
            setOpen(true);
          return true;
        }
        return false;
      }
    const backToButton = () => {
        // if (windowWidth < 1366) {
        //   navigate(`/story-levels?backto=${backToLabel}`);
        // } else {
        const backToUrl = getBackToUrl(backToLabel, CHAPTER_ID,isTablate,isMobile);
        if (backToUrl) {
            navigate(backToUrl);
        }
        // }
    };
    const bookmarkhandler = async () => {
        setbookmarloader(true)
        if (!bookBookmarks) {
            await dispatch(handleCreateBookmarks({
                bookId: CHAPTER_ID,
                page: page,
                userId: get(currentUser, "id", ""),
                classId: get(currentClass, "id", "")
            }))
        } else {
            if (bookBookmarks.page == page) {
                setbookmarloader(false)
                return;
            }
            await dispatch(handleUpdateBookmarks(bookBookmarks.id, page))
        }
        setbookmarloader(false)
    }
    const goToBookmark = () => {
        setPage(parseInt(bookBookmarks.page, 10));
        navigate(generateUrl(bookBookmarks.page));
    };
    const deleteBookMark = async () => {
        setDeleteBookmarloader(true)
        await dispatch(handleDeleteBookmarks(bookBookmarks.id))
        setRemoveBookMark(false)
        setDeleteBookmarloader(false)
    }
    const getPageContent = useMemo(() => {
        const start_page = parseInt(bookData?.start, 10);
        const end_page = parseInt(bookData?.finish, 10);
        if (start_page && end_page && data?.content) {
            const startIndex = findIndex(data.content, k => k.pageId == start_page)
            const endIndex = findIndex(data.content, k => k.pageId == end_page)
            if (startIndex >= 0 && endIndex >= 0) {
                return data.content.slice(startIndex, endIndex + 1);
            }
        }
        return [];
    }, [data, bookData])
    const getHyperlink = () => {
        let links = null;
        if (pageHyperlink) {
            links = pageHyperlink;
            if (ebookFlagTeacherEdition) {
                links = filter(pageHyperlink, ["type", "teacher_ed"])
            } else {
                links = null;
            }
        }
        return links;
    }

    const getPageHyperlink = () => pageHyperlink ? filter(pageHyperlink, k => k.type !== "teacher_ed") : null;

    const isTeacher = get(currentUser, "role") === "teacher";
    const [vocModal, setVocModal] = useState(false);
    const [phrasesModal, setPhrasesModal] = useState(false);
    const [cogModal,setCogModal] = useState(false);
    const [viewLessonDifferentiation, setViewLessonDifferentiation] = useState(false);
    const [viewLesson, setViewLesson] = useState(false);
    const [viewLessonType, setViewLessonType] = useState("");

    const [viewLessonTypeValue, setViewLessonTypeValue] = useState("");

    const [viewLessonTypeDiff, setViewLessonTypeDiff] = useState("");
    const [viewStreamLesson, setViewStreamLesson] = useState("");

    const [toolsDropdown, setToolsDropdown] = useState(false);
    const [shareWithMe, setShareWithMe] = useState(false);
    const [shareByMe, setShareByMe] = useState(false);

    const currentPageIndex = useMemo(() => {
        const pages = data?.content || [];
        const pageIndex = pages.findIndex(p => p.pageId == page);
        return pageIndex;
    }, [page, data]);

    const hasSuperLessonPage = useMemo(() => {
        if (!SuperLesson || !data?.content) return null;
        const pages = data.content;
        return find(SuperLesson, (p) => {
            const fromIndex = pages.findIndex(x => x.pageId == p.from);
            const toIndex = pages.findIndex(x => x.pageId == p.to);
            return currentPageIndex >= fromIndex && currentPageIndex <= toIndex && fromIndex >= 0 && toIndex >= 0;
        });
    }, [SuperLesson, currentPageIndex, data?.content]);

    const hasDifferentiationLessonPage = useMemo(() => {
        if (!DifferentiationLesson || !data?.content) return null;
        const pages = data.content;
        return find(DifferentiationLesson, (p) => {
            const fromIndex = pages.findIndex(x => x.pageId == p.from);
            const toIndex = pages.findIndex(x => x.pageId == p.to);
            return currentPageIndex >= fromIndex && currentPageIndex <= toIndex && fromIndex >= 0 && toIndex >= 0;
        });
    }, [DifferentiationLesson, currentPageIndex, data?.content]);

    const onLessonViewDiff = (val) => {
        updatePopUpAtOnce(true);
        setViewLessonDifferentiation(true);
        setViewLessonTypeDiff(val)
    }

    const onViewShowStreamData = (val) => {
        setViewLessonDifferentiation(true);
        setViewStreamLesson(val)
    }

    const onLessonView = (val) => {
        updatePopUpAtOnce(true);
        setViewLesson(true);
        setViewLessonType(val)
    }

    const onViewShowData = (value) => {
        setViewLesson(true);
        setViewLessonTypeValue(value)
    }

    const debounceHandleLearningModuleCount = useCallback(debounce((pageId) => {
        dispatch(handleLearningModuleCount({
            chapter: CHAPTER_ID,
            userId: currentUser?.role === 'student' ? currentClass?.teacherId : currentUser.id,
            pageId: pageId,
        }));
    }, 1000), [CHAPTER_ID, currentUser, currentClass, dispatch]);

    const disableBackWrad = () => {
        return currentPageIndex === 0;
    }
    const disableForWard = () => {
        return currentPageIndex >= data?.content?.length - 1;
    }

    const navigateBackward = () => {
        setViewLesson(false);
        setEbookFlag(false);
        revokeAiAnswerView();
        revokeAnswerView();
        revokeMainbookView();
        updatePopUpAtOnce(false);
        setViewLessonDifferentiation(false);
        setDifferentiationOpen(false);
        setSuperLessonOpen(false);
        revokePDFView();
        const pages = data?.content || [];
        if (currentPageIndex > 0) {
            const previousPage = pages[currentPageIndex - 1];
            debounceHandleLearningModuleCount(previousPage.pageId);
            navigate(generateUrl(previousPage.pageId));
            if (typeof clearPreviewRef.current == "function") {
                clearPreviewRef.current()
            }
            setFlagsFalse();
            if (currentFullScreen) {
                setTimeout(forceNoScroll, 50);
            }
        }
    }

    const navigateForward = () => {
        revokeAiAnswerView();
        revokeAnswerView();
        setViewLesson(false);
        setEbookFlag(false);
        revokeMainbookView();
        updatePopUpAtOnce(false);
        setViewLessonDifferentiation(false);
        setDifferentiationOpen(false);
        setSuperLessonOpen(false);
        revokePDFView();
        const pages = data?.content || [];
        if (currentPageIndex < pages.length - 1) {
            const nextPage = pages[currentPageIndex + 1];
            if(nextPage){
                debounceHandleLearningModuleCount(nextPage.pageId);
                navigate(generateUrl(nextPage.pageId));
            }
        }
        if (currentFullScreen) {
            forceNoScroll();
        }
        preloadImage()
        if (typeof clearPreviewRef.current == "function") {
            clearPreviewRef.current()
        }
        setFlagsFalse();
    }

    const onTwoSideChange = (page) => {
        preloadImage();
        if (typeof clearPreviewRef.current == "function") {
            clearPreviewRef.current()
        }
        navigate(generateUrl(parseInt(page, 10)));
        setPage(parseInt(page, 10));
        setFlagsFalse();
    }

    // const handleOpenSuperLessonToast = (message) => {
    //     setPopupMessage(message);
    //     setOpen(true);
    // }

    const handleOpenSuperLessonToast = () => {
        if(checkPopUpAtOnce()) return;
        updatePopUpAtOnce(true);
         if(UI?.openSuperLesson){
            showAlert("Super Lesson is already Opened", "error");
            return;
        }
        if(handleForDistrictAdmin()) return;
        if (isCanvasUser) {
            setOpenSuperLessonToolModal(true);
        } else {
            if (window.innerWidth >= 744) {
                if (differentiationOpen) {
                    setPopupMessage("Please close the Differentiation, to open the SuperLesson");
                    setOpen(true);
                } else if (storyCreateOpen) {
                    setPopupMessage("Please close the Story AI, to open the SuperLesson");
                    setOpen(true);
                } else {
                    setOpenSuperLessonToolModal(true)
                }
            } else {
                setPopupMessage("Super Lesson is designed for larger screens, and is not supported on smaller or mobile screens.");
                updatePopUpAtOnce(false);
                setOpen(true);
            }
        }
    }
    const handleOpenDifferentiationToast = () => {
        if(checkPopUpAtOnce()) return;
        updatePopUpAtOnce(true);
         if(UI?.openDiffModal){
            showAlert("Differnation is already Opened", "error");
            return;
        }
        if(handleForDistrictAdmin()) return;
        if (isCanvasUser) {
            setOpenDiffernationToolModal(true)
        } else {
            if (window.innerWidth >= 744) {
                if (superLessonOpen) {
                    setPopupMessage("Please close the SuperLesson, to open the Differentiation");
                    setOpen(true);
                } else if (storyCreateOpen) {
                    setPopupMessage("Please close the Story AI, to open the Differentiation");
                    setOpen(true);
                } else {
                    setOpenDiffernationToolModal(true)
                }
            } else {
                setPopupMessage("Differentiation is designed for larger screens, and is not supported on smaller or mobile screens.");
                updatePopUpAtOnce(false);
                setOpen(true);
            }
        }
    }

    const handleGenerateAiAssignment = () => {
        if(handleForDistrictAdmin()) return;
        setassignmentAiModal(true)
    }

    const RenderMenuContent = () => {
        const items = [
            ...(isTeacher ? [{
                key: 'instruction',
                label: 'Instruction',
                className: 'ulLitItmeList',
                onClick: () => {
                     if(checkPopUpAtOnce()) return;
                    updatePopUpAtOnce(true);
                    setInstModal(true);
                }
            }] : []),
            {
                key: 'vocabulary',
                label: 'Vocabulary',
                className: 'ulLitItmeList',
                onClick: () => {
                    setVocModal(true);
                }
            },
            // {
            //     key: 'phrases',
            //     label: 'Phrases',
            //     className: 'ulLitItmeList',
            //     onClick: () => {
            //         //  if(checkPopUpAtOnce()) return;
            //         // updatePopUpAtOnce(true);
            //         setPhrasesModal(true);
            //     }
            // },
            {
                key: 'cognates',
                label: 'Cognates',
                className: 'ulLitItmeList',
                onClick: () => {
                    setCogModal(true);
                }
            },
            ...(hasSuperLessonPage && isTeacher ? [{
                key: 'superLesson',
                label: 'Super Lesson',
                className: 'ulLitItmeList',
                onClick: () => handleOpenSuperLessonToast("Supper Lesson is not available for mobile")
            }] : []),
            ...((hasSuperLessonPage || hasDifferentiationLessonPage) && isTeacher ? [{
                key: 'differentiation',
                label: 'Differentiation',
                className: 'ulLitItmeList',
                onClick: () => handleOpenDifferentiationToast("Differentiation is designed for larger screens, and is not supported on smaller or mobile screens.")
            }] : []),
            // ...(currentUser?.role !== "student" ? [{
            //     key: 'generateAssignment',
            //     label: <div onClick={() => {
            //         if(checkPopUpAtOnce()) return;
            //         updatePopUpAtOnce(true);
            //         if(handleForDistrictAdmin()) return;
            //         setassignmentAiModal(true)
            //     }} className='differnation-tool'>Generate Assignment with AI</div>
            // }] : []),
            ...(currentUser?.role === "student" && Array.isArray(hasStoryWithAi) && hasStoryWithAi.length > 0 ? [{
                key: 'soyHistorias',
                label: 'Soy Historias',
                className: 'ulLitItmeList',
                onClick: () => {
                    if(checkPopUpAtOnce()) return;
                    updatePopUpAtOnce(true);
                    handleOpenStoryWithAi();
                }
            }] : []),
            ...(page_articles.length > 0 ? [{
                key: 'levels',
                label: 'Levels',
                className: 'ulLitItmeList',
                onClick: () => {
                    setToolsDropdown(false);
                    if(checkPopUpAtOnce()) return;
                    updatePopUpAtOnce(true);
                    setOpenArticleLevel(true);
                }
            }] : []),
            ...(currentUser?.role === "student" && checkInstructionDataForStudent ? [{
                key: 'playInstruction',
                label: 'Play Instruction',
                className: 'ulLitItmeList',
                onClick: () => {
                    if(checkPopUpAtOnce()) return;
                    updatePopUpAtOnce(true);
                    setInstModal(true);
                }
            }] : []),
            // ...(currentUser?.role !== "student" ? [{
            //     key: 'generateGrammar',
            //     label: <div onClick={() => {
            //         setIsOpen(true);
            //         setToolsDropdown(false);
            //     }} className='differnation-tool cursor-pointer'>Generate Grammar Drills</div>
            // }] : []),
            // ...(currentUser?.role !== "student" && Boolean(page_slide&&page_slide.id) ? [{
            //     key: 'ebookPresentation',
            //     label: 'Ebook Presentation',
            //     className: 'ulLitItmeList',
            //     onClick: () => {
            //         setToolsDropdown(false)
            //         handlerOpen(true)
            //     }
            // }] : [])
            ...(currentUser?.role !== "student" ? [{
                key: 'shareWithMe',
                className: 'ulLitItmeList',
                label: <div className='' onClick={() => {
                     if(checkPopUpAtOnce()) return;
                    updatePopUpAtOnce(true);
                    if(handleForDistrictAdmin()) return;
                    setShareWithMe(true)
                }}>Shared with me</div>
            }] : []),
            ...(currentUser?.role !== "student"  ? [{
                key: 'shareByMe',
                className: 'ulLitItmeList',
                label: <div className='' onClick={() => {
                     if(checkPopUpAtOnce()) return;
                    updatePopUpAtOnce(true);
                    if(handleForDistrictAdmin()) return;
                    setShareByMe(true)
                }}>Shared by me</div>
            }] : []),
        ];

        return { items };
    };

    const mediaQuery = 1451; // display after hide super lesson and differnation button

    const DisplaySuperLessonAndDiff = window.innerWidth < mediaQuery && currentUser?.role !== "student";
    
    useEffect(() => {
        if (currentFullScreen) {
            document.documentElement.style.overflow = 'hidden';
            document.body.style.overflow = 'hidden';
        } else {
            removeNoScroll();
        }
        return () => {
            removeNoScroll();
        };
    }, [currentFullScreen, location.pathname]);
      
    useEffect( () => {
        if(isTablet){
            if(!currentFullScreen && !landscapeOrientation){
                // forceNoScroll();
           }else if(currentFullScreen && !landscapeOrientation){
                // forceNoScroll();
           }else if(!currentFullScreen && landscapeOrientation){
                removeNoScroll();
           }else{
                removeNoScroll();
           }
        }else{
            if (currentFullScreen) {
                forceNoScroll();
              } else {
                removeNoScroll();
              }
        }
       
    }, [currentFullScreen,landscapeOrientation,isTablet]);

    const handleToolsClick = (e) => {
      e.stopPropagation(); // Prevent event bubbling
      if (checkPopUpAtOnce()) {
        return;
      }
      setToolsDropdown(true);
    };

    return (
    <SentryWrap>
        <>
            <Snackbar
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                open={open}
                autoHideDuration={3000}
                onClose={handleClose}>
                <Alert onClose={handleClose} severity="error">
                    <strong>ALERT:</strong> {popupMessage}
                </Alert>
            </Snackbar>
            <Modal
                open={removeBookMark}
                onClose={() => setRemoveBookMark(false)}
            ><ModalContent>
                    Are you sure you want to remove all bookmarks?
                    <ModalRow>
                        <Button
                            color="primary"
                            variant="contained"
                            style={{ marginRight: '4px' }}
                            onClick={() => {
                                setRemoveBookMark(false)
                            }}
                        >
                            No
                        </Button>
                        <Button
                            color="secondary"
                            variant="contained"
                            style={{ marginRight: '4px' }}
                            onClick={() => {
                                deleteBookMark()
                            }}
                        >
                            {deleteBookmarkloader ? <Loading size={18} white /> : "Yes"}
                        </Button>
                    </ModalRow>
                </ModalContent>
            </Modal>
            <Wrapper className={`${currentFullScreen && 'centered-element'} ${orientation && 'landscapePsace'}`}>
                <div className="arowEbok">
                <ButtonContainer
                    ref={backBtnRef}
                    disabled={disableBackWrad()}
                    onClick={navigateBackward}
                        left={12}
                        mobileLeft={0}
                        tabletLeft={0}
                >
                <LeftCircleOutlined style={{ fontSize: '52px', color: "#020A52", background: 'rgba(255, 255, 255, 0.7)', borderRadius: 30 }} />
                </ButtonContainer>

                <ButtonContainer
                    ref={forwardBtnRef}
                    disabled={disableForWard()}
                    onClick={navigateForward}
                    right={12}
                    mobileRight={0}
                    tabletRight={0}
                >
                    <RightCircleOutlined style={{ fontSize: '52px', color: "#020A52", background: 'rgba(255, 255, 255, 0.7)', borderRadius: 30 }} />
                </ButtonContainer>
                </div>
                <Container style={{ marginTop: currentFullScreen && !landscapeOrientation ? -50 : 'unset', marginBottom: landscapeOrientation ? 60: ''  }}>

                <ImageContainer isDrawerOpen={isMatchMakingAssignment && isDrawerOpen} className={`balconSuperLessonAddOnUpr ${currentFullScreen ? 'largTab' : ''}`} style={currentFullScreen && !landscapeOrientation ? { transform: "scale(0.8)" } : {}} onContextMenu={(e) => {
                    e.preventDefault();
                    showCopyRight()
                }}>
                    <Row>
                        <Col span={20} className={`balconSuperLessonAddOn ${orientation && 'balconSuperLessonsAddOn'}`} style={{ justifyContent: "flex-start", display: "flex", flexDirection: "row", alignItems: "end" }}>
                            <BackButton className='text-wrap min-w-50px backInformateCstm' style={{ marginRight: "1.5px", whiteSpace: "nowrap", display: "flex", alignItems: "center", padding: "8px", height: "36px", }} onClick={backToButton}>
                                <ArrowBack style={{ fontSize: '14px', marginRight: '4px' }} />
                                <Tooltip placement='top' title={`Back to ${backToLabel}`}>
                                    Back {window.innerWidth >= 1200 && `to ${backToLabel}`}
                                </Tooltip>
                            
                            </BackButton>
                            <div className="mobileTabs eBookTabLft eBookTabMob scheduledWebHide balcon-drawer">
                                    <div className="mobileTabDrop">
                                        <Dropdown menu={RenderMenuContent()} trigger={['click']}>
                                            <Button style={{ display: 'flex', alignItems: 'center' }} className="BtnMenuOptions">
                                                <MenuOutlined />
                                            </Button>
                                        </Dropdown>
                                    </div>
                                </div>
                            <Instructions pageId={page} bookId={chapter} instModal={instModal} setInstModal={setInstModal} from={"balcon"} setCheckInstructionDataForStudent={(value)=>setCheckInstructionDataForStudent(value)} closeToolsDropdown={() => setToolsDropdown(false)} />
                            <Vocabilary pageNumber={getNumberMask(page)} vocabilaryButtonClassName='vocab-button-balcon' pageId={page} bookId={CHAPTER_ID} style={{ borderRadius: "0px !importent" }} isCheckingOpen={vocModal} setVocModal={setVocModal} closeToolsDropdown={() => setToolsDropdown(false)} />
                            {/* <div className={currentUser?.role === "student" ? "ml-5px" : ""}>
                                <Phrases pageNumber={`${parseInt(page) - parseInt(bookData?.start) + 1}`} vocabilaryButtonClassName='vocab-button-balcon' pageId={page} bookId={CHAPTER_ID} style={{ borderRadius: "0px !importent" }} isCheckingOpen={phrasesModal} setVocModal={setPhrasesModal} />
                            </div> */}
                            <div className={currentUser?.role === "student" ? "ml-5px" : ""}>
                                <CognatesHandler pageNumber={getNumberMask(page)} non_spanish_vocab={false} vocabilaryButtonClassName='ebook-vocab-book' pageId={page} bookId={chapter} isCheckingOpen={cogModal} setVocModal={setCogModal} style={{ borderRadius: "0px !importent" }} closeToolsDropdown={() => setToolsDropdown(false)} />
                            </div>
                            <div style={{ position: "relative" }} className='relative '>
                                        {(currentUser?.role !== "student") && (
                                            <Button
                                        onClick={handleToolsClick}
                                        // onMouseEnter={() => setToolsDropdown(true)}
                                                style={{ minWidth: "unset", height: "36px", borderRadius: "0px !important"
                                                }}
                                                className="vocabularyBtnDiff toolButton-new"
                                            >
                                                Tools
                                            </Button>
                                        )} 
                                        {/* TODO enable after release */}
                                    {page_articles.length > 0 && (
                                        <Button
                                            onClick={() => {
                                                setToolsDropdown(false);
                                                if(checkPopUpAtOnce()) return;
                                                updatePopUpAtOnce(true);
                                                setOpenArticleLevel(true)}}
                                            style={{ minWidth: "unset", height: "36px", borderRadius: "0px !important" }}
                                            className="vocabularyBtnDiff toolButton-new"
                                        >
                                            Levels
                                        </Button>
                                    )}

                                    {toolsDropdown && (
                                    <ClickOutside className='dropdown-tooling-ebook' onClickOutside={() => setToolsDropdown(false)}>
                                        <DropdownWrapper>
                                        {(hasSuperLessonPage && currentUser?.role !== "student") ||
                                        (hasDifferentiationLessonPage && currentUser?.role !== "student") ? (
                                            <>
                                            {hasSuperLessonPage && currentUser?.role !== "student" && (
                                                <DropdownItem
                                                style={{ backgroundColor: UI?.openSuperLesson ? "lightGrey" : "" }}
                                                onClick={handleOpenSuperLessonToast}
                                                className='superlesson-tool'
                                                >
                                                Super Lesson
                                                </DropdownItem>
                                            )}

                                            {(hasSuperLessonPage  || hasDifferentiationLessonPage) && currentUser?.role !== "student" && (
                                                <DropdownItem
                                                style={{ backgroundColor: UI?.openDiffModal ? "lightGrey" : "" }}
                                                onClick={handleOpenDifferentiationToast}
                                                className='differnation-tool'
                                                >
                                                Differentiation
                                                </DropdownItem>
                                            )}
                                            {/* {currentUser?.role !== "student" ? (
                                                <DropdownItem onClick={() => {
                                                    if(handleForDistrictAdmin()) return;
                                                    setassignmentAiModal(true)
                                                }} className='differnation-tool'>Generate Assignment with AI</DropdownItem>
                                            ) : null} */}

                                            {/* {currentUser?.role !== "student"  && (
                                                <DropdownItem onClick={() => {
                                                    if(handleForDistrictAdmin()) return;
                                                    setIsOpen(true);
                                                    setToolsDropdown(false);
                                                }} className='differnation-tool'>Generate Grammar Drills</DropdownItem>
                                            )} */}

                                            



                                            {/* {slideWidget} */}
                                            {/* TODO enable after release */}
                                            </>
                                        ) : (
                                            <>
                                            </>
                                            // <div className="no-tool-message">
                                            //   No tools are available
                                            // </div>
                                        )}
                                        {currentUser?.role !== "student" && (
                                                <DropdownItem
                                                    onClick={() => {
                                                        if(checkPopUpAtOnce()) {
                                                            return;
                                                        }
                                                        setShareWithMe(true);
                                                        setToolsDropdown(false);
                                                    }}
                                                >
                                                        Shared with me
                                                </DropdownItem>
                                            )}

                                            {currentUser?.role !== "student" && (
                                                    <DropdownItem
                                                    onClick={() => {
                                                        if(checkPopUpAtOnce()) {
                                                            return;
                                                        }
                                                        setShareByMe(true);
                                                        setToolsDropdown(false);
                                                    }}
                                                >
                                                    Shared by me
                                                </DropdownItem>
                                            )}
                                        </DropdownWrapper>
                                    </ClickOutside>
                                    )}
                                    </div>
                                {hasSuperLessonPage && currentUser?.role !== "student" && (
                                    <SuperLessons
                                        showModal={true}
                                        viewLesson={viewLesson}
                                        superLessonData={hasSuperLessonPage}
                                        bookId={chapter}
                                        pageId={page}
                                        setVocModal={setVocModal}
                                        onLessonOpen={onLessonView}
                                        onSuperLessonOpen={(value) => {
                                            setSuperLessonOpen(value);
                                        }}
                                        onViewShowData={onViewShowData}
                                        data={data}
                                        differentiationOpen={differentiationOpen}
                                        from={'balcon'}
                                        setOpenSuperLessonToolModal={setOpenSuperLessonToolModal}
                                        setToolsDropdown={setToolsDropdown}
                                        openSuperLessonToolModal={openSuperLessonToolModal}
                                        mediaQuery={mediaQuery}
                                        DisplaySuperLessonAndDiff={DisplaySuperLessonAndDiff}
                                        content={getPageContent}
                                        superLessonConfig={{
                                            from: hasSuperLessonPage?.from,
                                            to: hasSuperLessonPage?.to
                                        }}
                                    />
                                )}
                                {(hasSuperLessonPage || hasDifferentiationLessonPage) && currentUser?.role !== "student" && (
                                    <Differentiation
                                        showModal={(hasSuperLessonPage || hasDifferentiationLessonPage) && currentUser?.role !== "student"}
                                        viewLesson={viewLessonDifferentiation}
                                        superLessonData={hasDifferentiationLessonPage || hasSuperLessonPage}
                                        bookId={chapter}
                                        pageId={page}
                                        setVocModal={setVocModal}
                                        onLessonOpen={onLessonViewDiff}
                                        data={data}
                                        superLessonOpen={superLessonOpen}
                                        onViewShowData={onViewShowStreamData}
                                        viewStreamLesson={viewStreamLesson}
                                        setViewStreamLesson={setViewStreamLesson}
                                        setIsStream={setIsStream}
                                        onDifferentiationOpen={(value) => {
                                            setDifferentiationOpen(value);
                                        }}
                                        from={'balcon'}
                                        setOpenDiffernationToolModal={setOpenDiffernationToolModal}
                                        setToolsDropdown={setToolsDropdown}
                                        openDiffernationToolModal={openDiffernationToolModal}
                                        mediaQuery={mediaQuery}
                                        DisplaySuperLessonAndDiff={DisplaySuperLessonAndDiff}
                                    />
                                )}
                                {currentUser?.role !== "student" && <AssignmentAiSection
                                    data={data}
                                    content={getPageContent}
                                    start_page={parseInt(bookData?.start || '0', 10)}
                                    finish_page={parseInt(bookData?.finish || '0', 10)}
                                    assignmentAiModal={assignmentAiModal}
                                    setassignmentAiModal={setassignmentAiModal}
                                    page={page}
                                    chapter={chapter}
                                    pageSrc={getSrcPage(page)}
                                    from={"balcon"}
                                    BALCON_PAGES={BALCON_PAGES?.length > 0 ? BALCON_PAGES.map((p,i)=>({...p,page_dynamic_id:i+1})) : dataContent?.map((p,i)=>({...p,page_dynamic_id:i+1}))}
                                />}

                            </Col>
                            <Col span={4} style={{ gap: "1.3px", justifyContent: "flex-end", display: "flex", flexDirection: "row", alignItems: "end", }}>
                                <ViewGallery pages={getPageContent} />
                                <TwoSidePages pageId={page} getAllPages={getPageContent} onTwoSideChange={(val) => onTwoSideChange(val)} onTwoSideButtonClick={() => preloadImage()} />
                                <BookMarkWrapper>
                                    {isTeacher && <BookmarkLinkWrapper style={{marginLeft: '1px'}}> <AddBookmarkButton
                                        color="primary"
                                        variant="contained"
                                        style={{ marginRight: bookBookmarks ? '4px' : 0 }}
                                        onClick={() => {
                                            bookmarkhandler()
                                        }}
                                    >
                                        {bookmarkloader ? <Loading size={20} white /> : <Bookmark color={get(bookBookmarks, "page", '') == page ? "secondary" : "action"} />}
                                    </AddBookmarkButton></BookmarkLinkWrapper>}
                                    {bookBookmarks ? (<BookmarkLinkWrapper>
                                        <MarkButton onClick={() => {
                                            goToBookmark()
                                        }}>
                                            Pg:{getNumberMask(get(bookBookmarks, "page", ''))}
                                        </MarkButton>
                                        {isTeacher && <TrashButton
                                            onClick={() => {
                                                setRemoveBookMark(true)
                                            }}
                                        >
                                            <Delete color='secondary' />
                                        </TrashButton>}

                                    </BookmarkLinkWrapper>) : null}
                                </BookMarkWrapper>
                                {isTabletFull ?
                                    <FullScreenBtn
                                        onClick={currentFullScreen ? ExitFullScreenMode : FullScreenMode}
                                        style={{
                                            minWidth: "unset", height: "36px", borderRadius: "0px !important"
                                        }}
                                    >
                                        {!currentFullScreen ? <Fullscreen style={{ color: 'white' }} /> : <FullscreenExit style={{ color: 'white' }} />}
                                    </FullScreenBtn>
                                    : null}

                                {!orientation && currentFullScreen && <ActivityBtn
                                    onClick={toggleDrawer}
                                    style={{
                                        minWidth: "unset", height: "36px", borderRadius: "0px !important"
                                    }}
                                >
                                    Activity
                                </ActivityBtn>}
                            </Col>
                        </Row>
                        <SuperLessonDiffList 
                                        shareWithMe={shareWithMe}
                                        setShareWithMe={setShareWithMe}
                                        shareByMe={shareByMe}
                                        setShareByMe={setShareByMe} 
                                        chapter={CHAPTER_ID}
                                        from={'balcon'}
                                    />
                        <EbookGallleryView getRef={getImageRef} onPageSelect={ind => {
                            navigate(generateUrl(ind))
                            setPage(ind)
                        }} viewPageAsIndex />
                        <div ref={imageRef} style={{ zIndex: 0 }} className='to_be_print setCstmPosition'>
                        {
                            hasSuperLessonPage && currentUser?.role !== "student" &&
                            <LessonViewModal
                                pageId={page}
                                bookId={chapter}
                                superLessonData={hasSuperLessonPage}
                                showModal={hasSuperLessonPage && currentUser?.role !== "student"}
                                open={viewLesson}
                                viewLessonType={viewLessonType}
                                onClose={async () => {
                                    updatePopUpAtOnce(false)
                                    setViewLesson(false)
                                    setSuperLessonOpen(false)
                                    await dispatch(updateUI({openSuperLesson: false}))
                                
                                }}
                                data={data}
                                from={'balcon'}
                                isCanvasUser={isCanvasUser}
                                viewLessonTypeValue={viewLessonTypeValue}
                                setViewLessonTypeValue={setViewLessonTypeValue}
                                setViewLesson={setViewLesson}
                                superLessonConfig={{
                                    from: hasSuperLessonPage?.from,
                                    to: hasSuperLessonPage?.to
                                }}
                            />
                        }
                        {openStoryWithAi &&
                            <StoryViewModal
                                open={openStoryWithAiView}
                                setOpenStoryWithAiView={setOpenStoryWithAiView}
                                viewLessonType={viewLessonType}
                                onClose={() => {
                                    updatePopUpAtOnce(false);
                                    setOpenStoryWithAi(false);
                                    setOpenStoryWithAiView(false);
                                    setStoryCreateOpen(false);
                                    
                                    if (UI?.soyHistoriasView?.isNotification) {
                                        dispatch(updateUI({
                                            soyHistoriasView: {
                                                open: false,
                                                data: null,
                                                isNotification: false
                                            }
                                        }));
                                    }
                                }} 
                                onMoreStory={() => {
                                    setOpenStoryWithAi(false);
                                    setOpenStoryWithAiView(false);
                                    handleOpenStoryWithAi('more_story');
                                    setStoryCreateOpen(true);
                                }}
                                isCanvasUser={isCanvasUser}
                                hasStoryWithAi={hasStoryWithAi}
                                handleStoryId={handleStoryId}
                                setHandleStoryStream={setHandleStoryStream}
                                handleStoryStream={handleStoryStream}
                                setHandleStoryStreamTitle={setHandleStoryStreamTitle}
                                handleStoryStreamTitle={handleStoryStreamTitle}
                                from={'ebook'}
                                imageConfiguration={imageConfiguration}
                            />
                        }
                        {
                            (hasSuperLessonPage || hasDifferentiationLessonPage) && currentUser?.role !== "student" &&
                            <DifferentiationViewModal
                            bookId={chapter}
                            pageId={page}
                            superLessonData={hasDifferentiationLessonPage}
                            showModal={hasDifferentiationLessonPage && currentUser?.role !== "student"}
                            open={viewLessonDifferentiation}
                            viewLessonType={viewLessonTypeDiff}
                            viewStreamLesson={viewStreamLesson}
                            isStream={isStream}
                            onClose={async () => {
                                updatePopUpAtOnce(false)
                                setViewLessonDifferentiation(false)
                                setDifferentiationOpen(false)
                                await dispatch(updateUI({openDiffModal: false}));
                            
                            }}
                            data={data}
                            from={'balcon'}
                            superLessonConfig={{
                                from: hasSuperLessonPage?.from,
                                to: hasSuperLessonPage?.to
                            }}
                            />
                        }
                    
                                    <SentenceStarterPopUp
                                    bookId={chapter}
                                    pageId={page}  
                                    currentUser={currentUser} 
                                    book={book}
                                    />
                        
                        <MainbookPopup getRef={getImageRef} />
                        <ShowBookPreviewExtensionActivity getRef={getImageRef} />
                        <AnswerPopup getRef={getImageRef} onClose={() => {
                            setEbookFlag(false);
                            setEbookFlagTeacherEdition(false);
                        }} />
                        <PDFPopup getRef={getImageRef} onClose={() => {
                            revokePDFView();
                        }} />
                        <ActivityStandardsPopup getRef={getImageRef} onClose={() => {
                            revokeActivityStandardsView();
                        }} />
                        <StoryAIPopup imageConfiguration={imageConfiguration} getRef={getImageRef} onClose={() => {
                            // setEbookFlag(false)
                        }} />
                        <ImageHighlighter imageUrl={getSrcPage(page)} userId={currentUser.id} pageId={`${page}`} bookId={chapter}  imageConfiguration={imageConfiguration} setImageConfiguration={setImageConfiguration} ebookFlagTeacherEdition={ebookFlagTeacherEdition}/>
                        <SentenceStarterBookPopUp getRef={getImageRef} />
                        {/* <Images src={getSrcPage(page)} /> */}
                    {showStoryModal && (
                        <CreateStoryModal
                            data={data}
                            open={showStoryModal}
                            content={getPageContent}
                            onClose={() => {
                                updatePopUpAtOnce(false);
                                setShowStoryModal(false);
                                setStoryCreateOpen(false);
                            }}
                            storyData={hasStoryWithAi}
                            onShowView={(id) => {
                                setHandleStoryId(id);
                                setOpenStoryWithAi(true);
                                setOpenStoryWithAiView(true);
                            }}
                            onShowStreamView={(val) => {
                                setHandleStoryStream(val);
                                setOpenStoryWithAi(true);
                                setOpenStoryWithAiView(true);
                            }}
                            onStoryTitle={(title) => {
                                setHandleStoryStreamTitle(title);
                                setOpenStoryWithAi(true);
                                setOpenStoryWithAiView(true);
                            }}
                            from={'ebook'}
                            is_balcon={true}
                            isCanvasUser={isCanvasUser}
                            page={page}
                            chapter={chapter}
                            imageConfiguration={imageConfiguration}
                        />
                    )}
                    </div>
                    <AssignableNotificationAnswer getRef={getImageRef} />
                    {openArticleLevel ?
                        <ViewArticleLevels
                            imageConfiguration={imageConfiguration}
                            open={openArticleLevel}
                            onClose={() => {
                                updatePopUpAtOnce(false);
                                setOpenArticleLevel(false)
                            }}
                            viewLessonType={viewLessonType}
                            setOpenArticleModalView={setOpenArticleLevel}
                            chapter={chapter}
                            page={page}
                            page_articles={page_articles}
                        />
                        : null}
                </ImageContainer>
                <ToolsContainer
                isDrawerOpen={isMatchMakingAssignment && isDrawerOpen}
                isTeacher={currentUser?.role != 'student'}
                    className={`${!orientation ? (currentFullScreen ? 'sidebar' : '') : ''} ${isDrawerOpen ? 'open setFullMatchMake' : ''
                        } ${isTablate ? 'tablet-device' : ''}`} style={orientation && currentFullScreen ? {marginTop:'0px'} : {}}
                >
                    <BalconCulturalTooling
                        classUsers={classUsers}
                        activity={activity}
                        chapter={CHAPTER_ID}
                        history={navigate}
                        limit={data?.content.length}
                        page={`${page}`}
                        quiz={quiz}
                        data={data}
                        showCopyRight={showCopyRight}
                        tips={page_tips}
                        existTeacherEdition={existTeacherEdition}
                        existShowAnswer={existShowAnswer}
                        ebookFlag={ebookFlag}
                        setEbookFlag={setEbookFlag}
                        ebookFlagTeacherEdition={ebookFlagTeacherEdition}
                        setEbookFlagTeacherEdition={setEbookFlagTeacherEdition}
                        showSpecialSupport={showSpecialSupport}
                        setShowSpecialSupport={setShowSpecialSupport}
                        studentViewFlag={studentViewFlag}
                        setStudentViewFlag={setStudentViewFlag}
                        handleImagePrint={handleImagePrint}
                        printLoader={printLoader}
                        setSubmitRef={setSubmitRef}
                        timeInt={timeInt}
                        timmer={timeIntRef.current}
                        onFinish={onFinish}
                        onPageChage={re => clearPreviewRef.current = re}
                        pageNumber={getNumberMask(page)}
                        lessonPlan={lessonPlan}
                        rubrics={rubrics}
                        assignmentPlan={assignmentPlan}
                        workbook_plan={workbook_plan}
                        main_book={main_book}
                        unitExam={unitExam}
                        id={id}
                        CHAPTER_ID={CHAPTER_ID} // chapter
                        bookInfo={bookData}
                        setPage={setPage}
                        links={getHyperlink()}
                        pageHyperlink={getPageHyperlink()}
                        dataBook={dataBook}
                        pageSrc={getSrcPage(page)}
                        imageConfiguration={imageConfiguration}
                        content={dataContent}
                        page_1game_ai_content={page_1game_ai_content}
                        book={book}
                        backToLabelStory={backToLabel}
                        handleOpenStoryWithAi={handleOpenStoryWithAi}
                        hasStoryWithAi={hasStoryWithAi}
                        handlerOpenPresentation={handlerOpen}
                        page_slide={page_slide}
                        handleGenerateAiAssignment={handleGenerateAiAssignment}
                        setToolsDropdown={setToolsDropdown}
                        storyId={id}
                        storyLevel={level}
                        BALCON_PAGES={BALCON_PAGES?.length > 0 ? BALCON_PAGES.map((p,i)=>({...p,page_dynamic_id:i+1})) : dataContent?.map((p,i)=>({...p,page_dynamic_id:i+1}))}
                    />
                </ToolsContainer>
                </Container>
            </Wrapper>
        </>
    </SentryWrap>
    );
};



function forceNoScroll() {
    if(window.innerWidth > 770){
    document.documentElement.classList.add('fullscreen-no-scroll');
    document.body.classList.add('fullscreen-no-scroll');
    document.documentElement.style.overflow = 'hidden';
    document.documentElement.style.overflowX = 'hidden';
    document.documentElement.style.overflowY = 'hidden';
    document.body.style.overflow = 'hidden';
    document.body.style.overflowX = 'hidden';
    document.body.style.overflowY = 'hidden';
    }
  }
  
  function removeNoScroll() {
    document.documentElement.classList.remove('fullscreen-no-scroll');
    document.body.classList.remove('fullscreen-no-scroll');
    document.documentElement.style.overflow = '';
    document.documentElement.style.overflowX = '';
    document.documentElement.style.overflowY = '';
    document.body.style.overflow = '';
    document.body.style.overflowX = '';
    document.body.style.overflowY = '';
  }

export default BalconLayout;
